import { useState, useEffect, useRef } from "react";
import CustomTooltip from "../Buttons/CustomTooltip";
import { showToast } from "../Buttons/Toaster";
import loading from "../../assests/loading .gif";
import nologs from "../../assests/nologs.gif";

// Big-file scans can emit a very large number of log lines. Re-rendering an
// ever-growing array on every single incoming websocket message makes each
// update slower than the last (effectively quadratic over the scan), which
// is what causes the tab to hang for large files. Capping how many log
// entries we keep/render bounds that cost regardless of scan size.
const MAX_STORED_LOGS = 500;

// Built once instead of on every call to highlightKeywords (which otherwise
// re-compiles this regex for every single rendered log line, every render).
const HIGHLIGHT_KEYWORDS = {
  green: ["completed", "successfully"],
  yellow: [
    "SQL",
    "T-SQL",
    "output",
    "progress",
    "Transforming",
    "Converting",
    "folder",
    "file",
  ],
  red: ["of", "table", "queries", "Transformimg"],
  purple: ["Started", "database", "data"],
  error: ["error", "failed"],
};
const HIGHLIGHT_REGEX = new RegExp(
  `\\b(${[
    ...HIGHLIGHT_KEYWORDS.green,
    ...HIGHLIGHT_KEYWORDS.yellow,
    ...HIGHLIGHT_KEYWORDS.red,
    ...HIGHLIGHT_KEYWORDS.purple,
    ...HIGHLIGHT_KEYWORDS.error,
  ].join("|")})\\b`,
  "gi"
);

function SourceProgress({
  jobId,
  jobName,
  onClose,
  onMinimize,
  isLogLoading,
  jobProcessCompleted,
}) {
  const [logs, setLogs] = useState([]);
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isStreaming, setIsStreaming] = useState(false);
  const [processCompleted, setProcessCompleted] = useState(false);
  const socketRef = useRef(null);
  const progressSocketRef = useRef(null);
  const isMountedRef = useRef(true);
  const intentionallyClosedRef = useRef(false);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;

  const formatTimestamp = (timestamp) =>
    new Date(timestamp).toLocaleTimeString();

  const logContainerStyle =
    (logs?.length || 0) > 0
      ? { height: "40vh", overflowY: "auto" }
      : {
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "40vh",
        };

  const highlightKeywords = (text) => {
    if (!text) return text;

    return text.split(HIGHLIGHT_REGEX).map((part, index) => {
      const lower = part.toLowerCase();
      if (HIGHLIGHT_KEYWORDS.green.some((word) => word.toLowerCase() === lower)) {
        return (
          <span key={index} style={{ color: "#57FFEF", fontWeight: "bold" }}>
            {part}
          </span>
        );
      } else if (
        HIGHLIGHT_KEYWORDS.yellow.some((word) => word.toLowerCase() === lower)
      ) {
        return (
          <span key={index} style={{ color: "#FFD03C", fontWeight: "bold" }}>
            {part}
          </span>
        );
      } else if (
        HIGHLIGHT_KEYWORDS.red.some((word) => word.toLowerCase() === lower)
      ) {
        return (
          <span key={index} style={{ color: "#F97583", fontWeight: "bold" }}>
            {part}
          </span>
        );
      } else if (
        HIGHLIGHT_KEYWORDS.purple.some((word) => word.toLowerCase() === lower)
      ) {
        return (
          <span key={index} style={{ color: "#B392F0", fontWeight: "bold" }}>
            {part}
          </span>
        );
      } else if (
        HIGHLIGHT_KEYWORDS.error.some((word) => word.toLowerCase() === lower)
      ) {
        return (
          <span key={index} style={{ color: "#ec9395", fontWeight: "bold" }}>
            {part}
          </span>
        );
      }
      return part;
    });
  };

  // Cleanup function for WebSockets
  const cleanupWebSockets = () => {
    intentionallyClosedRef.current = true;

    if (socketRef.current) {
      socketRef.current.onclose = null; // Remove onclose handler to prevent reconnect
      socketRef.current.onerror = null; // Remove error handler
      socketRef.current.onmessage = null; // Remove message handler
      socketRef.current.close();
      socketRef.current = null;
    }

    if (progressSocketRef.current) {
      progressSocketRef.current.onclose = null;
      progressSocketRef.current.onerror = null;
      progressSocketRef.current.onmessage = null;
      progressSocketRef.current.close();
      progressSocketRef.current = null;
    }

    reconnectAttemptsRef.current = 0;
  };

  const handleClose = () => {
    cleanupWebSockets();
    onClose();
  };

  const handleMinimize = () => {
    cleanupWebSockets();
    onMinimize();
  };

  const setupWebSocket = (socketRef, url, handlers) => {
    if (intentionallyClosedRef.current || !isMountedRef.current) return null;

    const token = sessionStorage.getItem("access_token");
    if (!token) {
      showToast("Authentication token missing", "error");
      setIsLoading(false);
      return null;
    }

    const socket = new WebSocket(url);
    
    socket.onopen = () => {
      if (!isMountedRef.current) return;
      reconnectAttemptsRef.current = 0;
      handlers.onOpen?.();
      if (socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ token }));
      }
    };

    socket.onmessage = (event) => {
      if (!isMountedRef.current) return;
      handlers.onMessage?.(event);
    };

    socket.onerror = (error) => {
      if (!isMountedRef.current) return;
      handlers.onError?.(error);
    };

    socket.onclose = (event) => {
      if (!isMountedRef.current || intentionallyClosedRef.current) return;
      
      // Only attempt reconnect if not completed and not intentionally closed
      if (!processCompleted && !intentionallyClosedRef.current) {
        reconnectAttemptsRef.current += 1;
        if (reconnectAttemptsRef.current <= maxReconnectAttempts) {
          const delay = Math.min(1000 * reconnectAttemptsRef.current, 5000);
          setTimeout(() => {
            if (!intentionallyClosedRef.current && isMountedRef.current) {
              handlers.onReconnect?.();
            }
          }, delay);
        } else {
          handlers.onMaxAttemptsReached?.();
        }
      }
    };

    return socket;
  };

  useEffect(() => {
    isMountedRef.current = true;
    intentionallyClosedRef.current = false;
    reconnectAttemptsRef.current = 0;

    if (!jobId) return;

    const connectLogsWebSocket = () => {
      const wsBase = process.env.REACT_APP_API_URL_SOURCE_PROFILER;
      const url = `${wsBase}/ws/logs/${jobId}`;
      
      socketRef.current = setupWebSocket(socketRef, url, {
        onOpen: () => {
          setIsLoading(false);
          setIsStreaming(true);
        },
        onMessage: (event) => {
          try {
            const msg = JSON.parse(event.data);
            const { event: type, data, error } = msg;

            if (error) {
              console.error("WebSocket error:", error);
              socketRef.current?.close();
              return;
            }

            if (type === "full_update") {
              const mapped = data.map((log) => ({
                timestamp: formatTimestamp(log.timestamp),
                message: log.message,
                type: log.type || "info",
                log_level: log.log_level || "N/A",
              }));
              // Keep only the most recent entries - big scans can emit
              // thousands of log lines, and rendering them all keeps
              // getting slower the longer the scan runs.
              setLogs(
                mapped.length > MAX_STORED_LOGS
                  ? mapped.slice(-MAX_STORED_LOGS)
                  : mapped
              );
            } else if (type === "incremental_update") {
              setLogs((prev) => {
                const updated = [
                  ...prev,
                  ...data.map((log) => ({
                    timestamp: formatTimestamp(log.timestamp),
                    message: log.message,
                    type: log.type || "info",
                    log_level: log.log_level || "N/A",
                  })),
                ];
                return updated.length > MAX_STORED_LOGS
                  ? updated.slice(-MAX_STORED_LOGS)
                  : updated;
              });
            } else if (type === "complete") {
              setProcessCompleted(true);
              setIsStreaming(false);
              setProgress(100);
            }
          } catch (err) {
            console.error("Error parsing log message:", err);
          }
        },
        onError: (error) => {
          console.error("Logs WebSocket error:", error);
          setIsStreaming(false);
        },
        onReconnect: () => {
          connectLogsWebSocket();
        },
        onMaxAttemptsReached: () => {
          showToast("Max reconnection attempts reached for logs", "error");
        }
      });
    };

    const connectProgressWebSocket = () => {
      const wsBase = process.env.REACT_APP_API_URL_SOURCE_PROFILER;
      const url = `${wsBase}/ws/profiler_progress/${jobId}`;
      
      progressSocketRef.current = setupWebSocket(progressSocketRef, url, {
        onMessage: (event) => {
          try {
            const msg = JSON.parse(event.data);
            console.log("Progress update:", msg);

            if (msg.event === "profiler_completed") {
              setProgress(100);
              setProcessCompleted(true);
            } else if (msg.data?.progress_percentage !== undefined) {
              const percentage = msg.data.progress_percentage;
              setProgress(percentage);
            } else if (msg.progress !== undefined) {
              setProgress(msg.progress);
            }
          } catch (err) {
            console.error("Error parsing progress message:", err);
          }
        },
        onError: (error) => {
          console.error("Progress WebSocket error:", error);
        },
        onReconnect: () => {
          connectProgressWebSocket();
        },
        onMaxAttemptsReached: () => {
          showToast("Max reconnection attempts reached for progress", "error");
        }
      });
    };

    connectLogsWebSocket();
    connectProgressWebSocket();

    return () => {
      isMountedRef.current = false;
      cleanupWebSockets();
    };
  }, [jobId, processCompleted]);

  return (
    <div
      className="right-bar3 p-3 d-flex flex-column"
      style={{ height: "100%" }}
    >
      <style>{`
        .progress-wrapper {
          background-color: #f5f5f5;
          border-radius: 12px;
          padding: 24px;
        }
        .section-title {
          font-size: 14px;
          font-weight: 600;
          color: #333;
          margin-bottom: 6px;
        }
        .log-entry {
          font-size: 13px;
          line-height: 1.4;
          color: #222;
          margin-bottom: 12px;
        }
        .log-timestamp {
          font-size: 11px;
          color: #888;
        }
        .log-container-wrapper {
          flex: 1;
          overflow-y: auto;
          margin-top: 16px;
          padding: 16px;
          background: #1f1f2d;
          border-radius: 4px;
        }
        .footer-buttons {
          display: flex;
          justify-content: space-between;
          background: white;
          padding-top: 16px;
        }
        .progress-container {
          margin-bottom: 16px;
        }
        .progress-bar-height {
          height: 8px;
          border-radius: 4px;
          background-color: #e9ecef;
        }
        .progress-colorclass {
          transition: width 0.3s ease;
        }
        .progress-text {
          font-size: 12px;
          margin-top: 4px;
          color: #6c757d;
        }
        .placeholder-line {
          width: 100%;
          height: 14px;
          background-color: rgba(160, 168, 181, 0.31);
          border-radius: 2px;
          animation: pulse 1.5s infinite;
        }
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.4; }
          100% { opacity: 1; }
        }
      `}</style>

      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="font-size-13 fw-medium text-dark">Live Status</h6>
        <CustomTooltip content="Close" placement="left">
          <i
            onClick={handleClose}
            className="mdi mdi-close close-trace cursor-pointer right-bar-toggle3 iconColor"
          />
        </CustomTooltip>
      </div>

      {/* Scan Progress Card */}
      <div className="step4css">
        <div className="d-flex justify-content-between align-items-center mb-3 font-size-12">
          <span className="fw-semibold text-dark">
            Scan in progress: <span className="fw-normal">{jobName}</span>
          </span>
          <span>{progress}%</span>
        </div>

        <div className="progress mb-3 step4Database">
          <div
            className="progress-colorclass"
            role="progressbar"
            style={{
              width: `${progress}%`,
              backgroundColor: progress === 100 ? "#28a745" : "#007bff",
            }}
            aria-valuenow={progress}
            aria-valuemin="0"
            aria-valuemax="100"
          />
        </div>
        <div className="progress-text">{progressText}</div>
      </div>

      {/* Logs Section */}
      <div className="log-container-wrapper">
        {isLoading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: "200px" }}
          >
            <img
              src={loading || "/placeholder.svg"}
              alt="Loading..."
              style={{ width: "20%", height: "auto" }}
            />
          </div>
        ) : logs?.length > 0 ? (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 2fr",
                gap: "16px",
                paddingBottom: "8px",
                fontWeight: "bold",
                fontSize: "13px",
                color: "white",
              }}
            >
              <div style={{ color: "white" }}>Timestamp</div>
              <div style={{ color: "white", marginLeft: "30px" }}>
                Log Level
              </div>
              <div style={{ color: "white" }}>Summary</div>
            </div>

            {logs.map((log, index) => (
              <div
                key={index}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 2fr",
                  gap: "16px",
                  padding: "6px 0",
                  fontSize: "12px",
                  fontFamily: "'JetBrains Mono', monospace",
                  color: "white",
                  alignItems: "start",
                }}
              >
                <div style={{ wordBreak: "break-word" }}>{log.timestamp}</div>
                <div
                  style={{
                    wordBreak: "break-word",
                    marginLeft: "30px",
                    color:
                      log.log_level === "ERROR"
                        ? "#ec9395"
                        : log.log_level === "WARN"
                        ? "#FFD03C"
                        : log.log_level === "INFO"
                        ? "#57FFEF"
                        : "white",
                  }}
                >
                  {log.log_level}
                </div>
                <div style={{ wordBreak: "break-word" }}>
                  {highlightKeywords(log.message)}
                </div>
              </div>
            ))}

            {isStreaming && !processCompleted && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 2fr",
                  gap: "16px",
                  padding: "6px 0",
                }}
              >
                <div className="placeholder-line" />
                <div
                  className="placeholder-line"
                 
                />
                <div>
                  <div className="placeholder-line" />
                  <div
                    className="placeholder-line"
                    style={{ marginTop: "4px", width: "80%" }}
                  />
                </div>
              </div>
            )}
          </>
        ) : (
          <div
            className="d-flex flex-column justify-content-center align-items-center"
            style={{ height: "100%", minHeight: "200px" }}
          >
            <img
              src={nologs || "/placeholder.svg"}
              alt="No Logs..."
              style={{ width: "30%", height: "auto", maxWidth: "250px" }}
            />
            <p className="text-muted mt-3">No Logs Available</p>
          </div>
        )}
      </div>

      {/* Footer Buttons */}
      <div className="footer-buttons">
        <button
          className="btn btn-closebtn custom-button"
          onClick={handleClose}
        >
          Close
        </button>
        <button
          className="btn btn-primary custom-button"
          onClick={handleMinimize}
        >
          Minimize and Continue in Background
        </button>
      </div>
    </div>
  );
}

export default SourceProgress;